import { SaveOutlined } from '@ant-design/icons';
import { Checkbox, Col, Form, Input, InputNumber, message, Modal, Row, Select, Tag } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import { PassengerTypeLabel, TariffCategoryType } from '@/constants';
import i18n from '@/helpers/i18n';
import CreateBtn from '@/components/app/CreateBtn';
import BooleanTag from '@/components/app/BooleanTag';
import { clearAllSellOnTheGoFareConfigurationTariffParametersCache, clearAllTariffParametersCache } from '@/hooks/data/masterDataHook';
import { TariffParameterDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
import BaseTariffParameterInput from '@/components/FormItems/BaseTariffParameterInput';

const columns: IColumnType<TariffParameterDto>[] = [
  {
    title: 'base.Code',
    dataIndex: 'code',
  },
  {
    title: 'base.Name',
    dataIndex: 'name',
  },

  {
    title: 'companySettingsPage_TariffCategory',
    dataIndex: 'category',
    render: v => <Tag>{TariffCategoryType.__getValue(v)}</Tag>,
  },

  {
    title: 'companySettingsPage_BaseTariffParameter',
    dataIndex: 'baseTariffParameterName',
  },
  {
    // P4 : type de passager du catalogue global, en lecture seule pour la compagnie.
    title: 'passengerType_Title',
    dataIndex: 'passengerType',
    render: v => <Tag>{i18n.t(PassengerTypeLabel[v || 1])}</Tag>,
  },
  {
    // P4 : limites d'âge du tarif (bornes comprises, à la date du départ).
    title: 'tariffAge_Column',
    dataIndex: 'minAge',
    render: (_, record) => <TariffAges minAge={record.minAge} maxAge={record.maxAge} />,
  },
  {
    title: 'base.IsStandard',
    dataIndex: 'isStandard',
    render: v => <BooleanTag value={v} />,
  },
  {
    title: 'base.IsActive',
    dataIndex: 'isActive',
    render: v => <BooleanTag value={v} />,
  },
];

const TariffAges = ({ minAge, maxAge }: { minAge?: number; maxAge?: number }) => {
  const { t } = useTranslation();
  if (minAge == null && maxAge == null) return <span>{t('tariffAge_None')}</span>;
  if (maxAge == null) return <span>{t('tariffAge_From', { min: minAge })}</span>;
  if (minAge == null) return <span>{t('tariffAge_UpTo', { max: maxAge })}</span>;
  return <span>{t('tariffAge_Between', { min: minAge, max: maxAge })}</span>;
};

const MAX_AGE_LIMIT = 120;

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'searchText', label: 'companySettingsPage_TariffParamsSarchText' },
  { type: 'LIST', name: 'category', label: 'companySettingsPage_TariffCategory', listData: TariffCategoryType.__getSelectOptions() },
];

const TariffParameterTab = () => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const clearCache = () => {
    clearAllTariffParametersCache();
    clearAllSellOnTheGoFareConfigurationTariffParametersCache();
  };
  const createEntity = values => {
    setLoading(true);
    apiSdk.TariffParameterService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.TariffParameterService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const category = Form.useWatch('category', form);
  const passengerType = Form.useWatch('passengerType', form);
  return (
    <div>
      <ZTable
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        url="/fleets/tariff-parameters/get-tariff-parameters"
        bordered
        order_by="createdOn"
        order_by_asc={true}
        allowEdit={true}
        onEdit={record => {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue({
            ...record,
          });
        }}
        allowDelete={true}
        delete_url="/fleets/tariff-parameters/delete"
        filters={filters}
        searchBtnAlignLeft
        extraBtns={
          <CreateBtn
            onClick={() => {
              setIsNewForm(true);
              setFormVisible(true);
              form.resetFields();
            }}
          />
        }
        onDeleteCompleted={clearCache}
      ></ZTable>
      {formVisible ? (
        <Modal
          open={formVisible}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={isNewForm ? t('companySettingsPage_CreateTariffParams') : t('companySettingsPage_UpdateTariffParams')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateEntity(values);
                } else {
                  createEntity(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={6}>
              <Col md={24}>
                <Form.Item label={t('base.Code')} name="code" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
                <Form.Item label={t('base.Name')} name="name" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
                <Form.Item label={t('companySettingsPage_TariffCategory')} name="category" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Select
                    options={TariffCategoryType.__getSelectOptions()}
                    onChange={() => {
                      form.setFieldValue('baseTariffParameterId', null);
                    }}
                    disabled={!isNewForm}
                    className="input-value-color"
                  />
                </Form.Item>
                <Form.Item
                  label={t('companySettingsPage_BaseTariffParameter')}
                  name="baseTariffParameterId"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <BaseTariffParameterInput category={category} disabled={!isNewForm} className="input-value-color" />
                </Form.Item>
                {!isNewForm ? (
                  <Form.Item label={t('passengerType_Title')} tooltip={t('passengerType_ReadOnly')}>
                    <Tag data-testid="tariff-passenger-type">{t(PassengerTypeLabel[passengerType || 1])}</Tag>
                  </Form.Item>
                ) : null}
                <Form.Item name="passengerType" className="hidden">
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item
                  label={t('tariffAge_MinAge')}
                  name="minAge"
                  tooltip={t('tariffAge_Help')}
                  rules={[{ type: 'integer', min: 0, max: MAX_AGE_LIMIT, message: t('TariffAgeLimitsInvalid') }]}
                >
                  <InputNumber className="w-100" min={0} max={MAX_AGE_LIMIT} precision={0} placeholder={t('tariffAge_NoLimit')} />
                </Form.Item>
                <Form.Item
                  label={t('tariffAge_MaxAge')}
                  name="maxAge"
                  tooltip={t('tariffAge_Help')}
                  dependencies={['minAge']}
                  rules={[
                    { type: 'integer', min: 0, max: MAX_AGE_LIMIT, message: t('TariffAgeLimitsInvalid') },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const min = getFieldValue('minAge');
                        return value == null || min == null || min <= value
                          ? Promise.resolve()
                          : Promise.reject(new Error(t('TariffAgeLimitsInvalid')));
                      },
                    }),
                  ]}
                >
                  <InputNumber className="w-100" min={0} max={MAX_AGE_LIMIT} precision={0} placeholder={t('tariffAge_NoLimit')} />
                </Form.Item>
                <Form.Item label={t('base.IsStandard')} name="isStandard" valuePropName="checked">
                  <Checkbox />
                </Form.Item>
                <Form.Item label={t('base.IsActive')} name="isActive" valuePropName="checked">
                  <Checkbox />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      ) : null}
    </div>
  );
};
export default TariffParameterTab;
